import { eachDay, isoWeekday, weekStart, type ISODate } from './dates';
import { holidaysInRange, type HolidayOverride } from './holidays';

/**
 * Per day and person:
 * - target (Soll) = contractual working time minus public holidays minus absences, except time off in lieu
 *   (Freizeitausgleich): that day is paid from extra hours worked earlier (e.g. on a weekend), so it stays
 *   part of the target and lowers the balance of booked minus target hours;
 * - capacity (available) = target minus time off in lieu: the hours the person is actually there, used for
 *   planning, workload and the check for missing bookings.
 * Daily base = weekly hours / number of working days (the even split is shown in the UI).
 */

/** Absence kinds taken from extra hours worked (Freizeitausgleich): not deducted from the target. */
export const COMPENSATION_KINDS: readonly string[] = ['overtime'];

export interface ContractTerm {
  validFrom: ISODate;
  weeklyHours: number;
  /** ISO weekdays 1 (Mon) .. 7 (Sun) */
  workdays: number[];
}

export interface AbsenceSpan {
  from: ISODate;
  to: ISODate;
  halfDay: boolean;
  /** Half day: morning (default) or afternoon. */
  halfDayPart?: 'am' | 'pm' | null;
  /** Absence of some hours on a single day (e.g. doctor's appointment); null = whole or half day. */
  minutes?: number | null;
  /** Start of an absence of some hours ("HH:MM"), if known. */
  startTime?: string | null;
  kind?: string;
}

export interface PersonCalendarInput {
  entryDate?: ISODate | null;
  exitDate?: ISODate | null;
  contracts: ContractTerm[];
  holidayCalendar: string;
  absences: AbsenceSpan[];
  overrides?: HolidayOverride[];
  /**
   * Saturdays inside leave count as leave days although the contract has no hours on them (Turkey, 5-day week, when
   * the contract does not make Saturday a weekly rest day; setting leave.saturdayLeaveTR, request 24 of round 4).
   * Only the leave days change, never the target hours.
   */
  saturdayLeave?: boolean;
}

export interface DayCapacity {
  date: ISODate;
  workday: boolean;
  baseMinutes: number;
  holidayMinutes: number;
  absenceMinutes: number;
  /** available hours: target minus time off in lieu */
  capacityMinutes: number;
  /** Soll: contract minus holidays minus absences other than time off in lieu */
  targetMinutes: number;
  /** time off in lieu (Freizeitausgleich) on this day */
  compensationMinutes: number;
  holidayName?: string;
  absenceKind?: string;
}

export function contractAt(contracts: ContractTerm[], date: ISODate): ContractTerm | undefined {
  let found: ContractTerm | undefined;
  for (const c of contracts) {
    if (c.validFrom <= date && (!found || c.validFrom > found.validFrom)) found = c;
  }
  return found;
}

/**
 * The working day in two halves: morning and afternoon (base / 2 each). A half public holiday is the afternoon
 * (e.g. Arife in Turkey, 24 and 31 December by company rule). A whole-day absence covers the halves that are
 * not a holiday, a half-day absence its half; absences of some hours come on top, together at most the time
 * left. Morning and afternoon of one day can be separate absences (e.g. half a day of leave and half a day
 * of time off in lieu).
 */
export function dailyCapacity(input: PersonCalendarInput, from: ISODate, to: ISODate): DayCapacity[] {
  const holidays = holidaysInRange(input.holidayCalendar, from, to, input.overrides ?? []);
  return eachDay(from, to).map((date) => {
    const contract = contractAt(input.contracts, date);
    const employed =
      (!input.entryDate || date >= input.entryDate) && (!input.exitDate || date <= input.exitDate);
    const workday = !!contract && employed && contract.workdays.includes(isoWeekday(date));
    const base = workday && contract && contract.workdays.length > 0
      ? Math.round((contract.weeklyHours * 60) / contract.workdays.length)
      : 0;
    const holiday = holidays.get(date);
    const fraction = !holiday || holiday.fraction <= 0 ? 0 : holiday.fraction >= 1 ? 1 : 0.5;
    const pmHalf = Math.round(base / 2);
    const amHalf = base - pmHalf;
    const holidayMinutes = fraction === 1 ? base : fraction === 0.5 ? pmHalf : 0;
    let amKind: string | undefined;
    let pmKind: string | undefined;
    const hourly: AbsenceSpan[] = [];
    for (const a of input.absences) {
      if (date < a.from || date > a.to) continue;
      const single = a.from === a.to;
      if (single && a.minutes) {
        hourly.push(a);
        continue;
      }
      const part = single && a.halfDay ? (a.halfDayPart === 'pm' ? 'pm' : 'am') : 'day';
      if (part !== 'pm' && amKind === undefined) amKind = a.kind ?? 'absence';
      if (part !== 'am' && pmKind === undefined) pmKind = a.kind ?? 'absence';
    }
    const byKind = new Map<string, number>();
    const add = (kind: string, m: number) => {
      if (m > 0) byKind.set(kind, (byKind.get(kind) ?? 0) + m);
    };
    if (amKind !== undefined && fraction < 1) add(amKind, amHalf);
    if (pmKind !== undefined && fraction === 0) add(pmKind, pmHalf);
    let open = base - holidayMinutes - [...byKind.values()].reduce((x, m) => x + m, 0);
    for (const a of hourly) {
      const m = Math.max(0, Math.min(open, a.minutes ?? 0));
      add(a.kind ?? 'absence', m);
      open -= m;
    }
    let absenceMinutes = 0;
    let compensationMinutes = 0;
    let absenceKind: string | undefined;
    let top = 0;
    for (const [kind, m] of byKind) {
      absenceMinutes += m;
      if (COMPENSATION_KINDS.includes(kind)) compensationMinutes += m;
      if (m > top) {
        top = m;
        absenceKind = kind === 'absence' ? undefined : kind;
      }
    }
    const capacityMinutes = Math.max(0, base - holidayMinutes - absenceMinutes);
    return {
      date,
      workday,
      baseMinutes: base,
      holidayMinutes,
      absenceMinutes,
      capacityMinutes,
      targetMinutes: capacityMinutes + compensationMinutes,
      compensationMinutes,
      holidayName: holiday?.name,
      absenceKind,
    };
  });
}

/** Part of the day an absence claims: whole day, morning, afternoon or some hours. */
export function absencePart(a: AbsenceSpan): 'day' | 'am' | 'pm' | 'hours' {
  if (a.from !== a.to) return 'day';
  if (a.minutes) return 'hours';
  if (a.halfDay) return a.halfDayPart === 'pm' ? 'pm' : 'am';
  return 'day';
}

const clock = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/**
 * Two absences of one person claim the same time: shared days, unless one is the morning and the other the
 * afternoon, or one covers some hours next to a half day, or both cover hours at different times of the day.
 */
export function absencesConflict(a: AbsenceSpan, b: AbsenceSpan): boolean {
  if (a.to < b.from || b.to < a.from) return false;
  const pa = absencePart(a);
  const pb = absencePart(b);
  if (pa === 'day' || pb === 'day') return true;
  if (pa === 'hours' && pb === 'hours') {
    if (!a.startTime || !b.startTime) return true;
    const s1 = clock(a.startTime);
    const s2 = clock(b.startTime);
    return s1 < s2 + (b.minutes ?? 0) && s2 < s1 + (a.minutes ?? 0);
  }
  if (pa === 'hours' || pb === 'hours') return false;
  return pa === pb;
}

export interface WeekCapacity {
  weekStart: ISODate;
  baseMinutes: number;
  holidayMinutes: number;
  absenceMinutes: number;
  capacityMinutes: number;
  targetMinutes: number;
  compensationMinutes: number;
}

export function weeklyCapacity(days: DayCapacity[]): WeekCapacity[] {
  const map = new Map<ISODate, WeekCapacity>();
  for (const d of days) {
    const ws = weekStart(d.date);
    const w = map.get(ws) ?? { weekStart: ws, baseMinutes: 0, holidayMinutes: 0, absenceMinutes: 0, capacityMinutes: 0, targetMinutes: 0, compensationMinutes: 0 };
    w.baseMinutes += d.baseMinutes;
    w.holidayMinutes += d.holidayMinutes;
    w.absenceMinutes += d.absenceMinutes;
    w.capacityMinutes += d.capacityMinutes;
    w.targetMinutes += d.targetMinutes;
    w.compensationMinutes += d.compensationMinutes;
    map.set(ws, w);
  }
  return [...map.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

export function sumCapacity(days: DayCapacity[]): number {
  return days.reduce((s, d) => s + d.capacityMinutes, 0);
}
